import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import type { ReactNode } from 'react'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Card } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'
import { SparkBars } from '~/shared/ui/stacked-bars/spark-bars'
import { StackedBars } from '~/shared/ui/stacked-bars/stacked-bars'
import type { StackedBarsPoint } from '~/shared/ui/stacked-bars/stacked-bars-data'
import { formatChange, weekOverWeek } from './stats-derive'
import { formatCount } from './stats-format'

export type HeroMetricKey = 'active' | 'signups' | 'plansCreated' | 'peopleJoined'

type HeroState = 'ready' | 'loading' | 'error'

export interface HeroMetric {
  key: HeroMetricKey
  label: string
  explain: string
  color: string
  state: HeroState
  value?: number
  detail?: string
  daily?: readonly { day: string; value: number }[]
}

function Delta({ change, locale }: { change: number; locale: string }) {
  const up = change > 0
  const Icon = up ? ArrowUpRight : ArrowDownRight
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 font-medium tabular-nums',
        up ? 'text-[var(--metric-up)]' : 'text-muted-foreground',
      )}
    >
      {change !== 0 && <Icon className="size-3.5" aria-hidden />}
      {formatChange(change, locale)}
    </span>
  )
}

function HeroTile({
  metric,
  selected,
  onSelect,
  locale,
}: {
  metric: HeroMetric
  selected: boolean
  onSelect: () => void
  locale: string
}) {
  const values = metric.daily?.map((entry) => entry.value) ?? []
  const change = weekOverWeek(values)
  const ready = metric.state === 'ready' && metric.value !== undefined

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'relative flex min-w-0 flex-col gap-3 overflow-hidden rounded-xl border bg-card p-4 text-left sm:p-5',
        'transition-[border-color,box-shadow,background-color] duration-200 ease-out motion-reduce:transition-none',
        'focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2',
        selected
          ? 'border-foreground/20 shadow-[0_1px_2px_0_color-mix(in_srgb,var(--foreground)_6%,transparent)]'
          : 'hover:bg-foreground/2',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'absolute inset-x-0 top-0 h-0.5 transition-opacity duration-200 motion-reduce:transition-none',
          selected ? 'opacity-100' : 'opacity-0',
        )}
        style={{ backgroundColor: metric.color }}
      />
      <span className="flex items-center gap-2 text-muted-foreground text-xs">
        <i aria-hidden className="size-2 rounded-[2px]" style={{ backgroundColor: metric.color }} />
        <span className="truncate font-medium">{metric.label}</span>
      </span>

      {metric.state === 'loading' ? (
        <span className="space-y-2" aria-hidden>
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-3 w-28" />
        </span>
      ) : (
        <>
          <span className="flex items-end justify-between gap-3">
            <span className="font-semibold font-serif text-3xl text-foreground tabular-nums leading-none tracking-tight">
              {ready ? formatCount(metric.value ?? 0, locale) : '–'}
            </span>
            {values.length > 1 && (
              <span style={{ color: metric.color }}>
                <SparkBars values={values} width={56} height={22} />
              </span>
            )}
          </span>
          <span className="flex flex-col gap-0.5 text-xs">
            {change !== null && (
              <span className="flex flex-wrap items-baseline gap-x-1.5">
                <Delta change={change} locale={locale} />
                <span className="text-muted-foreground">{m.statsVsPreviousWeek()}</span>
              </span>
            )}
            {metric.detail && (
              <span className="truncate text-muted-foreground tabular-nums">{metric.detail}</span>
            )}
          </span>
        </>
      )}
    </button>
  )
}

export function HeroTiles({
  metrics,
  selected,
  onSelect,
  locale,
}: {
  metrics: readonly HeroMetric[]
  selected: HeroMetricKey
  onSelect: (key: HeroMetricKey) => void
  locale: string
}) {
  return (
    <fieldset className="m-0 grid min-w-0 grid-cols-2 gap-3 border-0 p-0 lg:grid-cols-4">
      <legend className="sr-only">{m.statsChooseMetric()}</legend>
      {metrics.map((metric) => (
        <HeroTile
          key={metric.key}
          metric={metric}
          selected={metric.key === selected}
          onSelect={() => onSelect(metric.key)}
          locale={locale}
        />
      ))}
    </fieldset>
  )
}

export function HeroChart({
  metric,
  locale,
  errorSlot,
}: {
  metric: HeroMetric
  locale: string
  errorSlot?: ReactNode
}) {
  const series: StackedBarsPoint<'value'>[] = (metric.daily ?? []).map((entry) => ({
    date: entry.day,
    values: { value: entry.value },
  }))

  return (
    <Card className="flex min-w-0 flex-col gap-4 p-5 sm:p-6">
      {metric.state === 'error' ? (
        errorSlot
      ) : (
        <StackedBars
          series={series}
          segments={[{ key: 'value', label: metric.label, color: metric.color }]}
          title={metric.label}
          subtitle={metric.explain}
          loading={metric.state === 'loading'}
          emptyText={m.statsChartEmpty()}
          height={200}
          locale={locale}
        />
      )}
    </Card>
  )
}
